import { formatNumber } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Input,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";

import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountMarketplace,
  Currency,
  KeywordTopOfSearchRankings,
  MatchType,
  SearchTermRank,
  StatsApi,
  StrategyApi,
  UpdateStrategyTopOfSearchRankingsActionEnum,
} from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  Metric,
  ROAS,
} from "@front/m19-metrics";
import { AdStatsEx, StrategyEx } from "@front/m19-models";
import {
  AccountSelectionService,
  adStatsGroupBy,
  AuthService,
  BrandAnalyticsService,
  getAmazonSearchUrl,
  MetricsSelectorPage,
  UserSelectionService,
} from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  IButtonDirective,
  IGridBadgeComponent,
  IToggleGridComponent,
  ModalOptions,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { MapUtils, mergeSeveralDates, sumAdStats } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { SubStrategyStats } from "@m19-board/models/Metric";
import { ASINS_WITH_TOP_OF_SEARCH_RANKINGS_FIRED } from "@m19-board/models/MetricsDef";
import { AsinLinkComponent } from "@m19-board/product-view/asin-link.component";
import { ProductThumbnailGridComponent } from "@m19-board/product-view/product-thumbnail-grid.component";
import { ProductViewComponent } from "@m19-board/product-view/product-view.component";
import {
  ChartData,
  ChartModalComponent,
  ChartModalInputData,
} from "@m19-board/shared/chart-modal/chart-modal.component";
import { LinkComponent } from "@m19-board/shared/link/link.component";
import { keywordRankingAvailableFor } from "@m19-board/tracking/KeywordRankingAvailability";
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay, switchMap } from "rxjs";
import { actionColumnProperties, ImageColumn } from "../../../grid-config/grid-columns";
import { restoreDefaultColumns, STATUS_BAR } from "../../../grid-config/grid-config";
import { GridViewerComponent, GridViewerModalData } from "../../../shared/grid-viewer/grid-viewer.component";

@UntilDestroy()
@Component({
  selector: "app-targeting-asin-stats",
  templateUrl: "./targeting-asin-stats.component.html",
  imports: [TranslocoDirective, AgGridAngular, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TargetingAsinStatsComponent implements OnInit {
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private statsService = inject(StatsApi);
  private brandAnalyticsService = inject(BrandAnalyticsService);
  private strategyService = inject(StrategyApi);
  private toasterService = inject(ToasterService);
  private accountSelectionService = inject(AccountSelectionService);
  private translocoService = inject(TranslocoService);
  private modalService = inject(ModalService);

  private readonly gridConfigService = inject(GridConfigService);

  strategy = input.required<StrategyEx>();
  strategyEx = computed(() => {
    const strategy = this.strategy();
    return strategy.isSpStrategy() || strategy.isSbStrategy() ? strategy : undefined;
  });

  @Input() isReadOnly: boolean = false;

  @Input() withTOSROSwitch = true;

  private readonly metrics: Metric<AdStatsEx>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    CPC,
    COST,
    CLICKS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    ACOS,
    ROAS,
  ];

  private readonly metricsWithTopOfSearch: Metric<AdStatsEx>[] = this.metrics.concat(
    ASINS_WITH_TOP_OF_SEARCH_RANKINGS_FIRED,
  );

  readonly sfrByKeyword: Map<string, number> = new Map();
  topOfSearchRankingsOn: Set<string> = new Set<string>();
  private selectedAccountMarketplace: AccountMarketplace | undefined = undefined;

  selectedMainMetrics$!: BehaviorSubject<Metric<AdStatsEx>[]>;

  locale = toSignal(this.authService.userLocale$, { requireSync: true });

  currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });

  kwStats?: AdStatsEx[];

  supportedMarketplace!: boolean;

  private readonly SFR_FALLBACK = 300000;
  private readonly GRID_KEY = "spKeywordsGrid2";

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  // <keyword::matchType, asinDetails>
  private asinDetailsByKeywordMatch?: Map<string, AdStatsEx[]>;
  private selectedChartKwRow$ = new BehaviorSubject<AdStatsEx | undefined>(undefined);
  gridData = signal<AdStatsEx[]>([]);

  private colDefs: ColDef[] = [
    {
      field: "keywordText",
      headerName: this.translocoService.translate("targeting-asin-stats.targeting", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("targeting-asin-stats.targeting"),
      pinned: "left",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      cellRendererSelector: (params) => {
        if (params.node.isRowPinned()) return undefined;
        const data = params.data;
        if (data) {
          if (data.matchType == MatchType.asinSameAs) {
            return {
              component: ProductViewComponent,
              params: { asin: params.value, marketplace: this.selectedAccountMarketplace?.marketplace },
            };
          } else {
            const link = getAmazonSearchUrl(params.value, this.selectedAccountMarketplace!.marketplace);
            return {
              component: LinkComponent,
              params: {
                href: link,
                target: "_blank",
                content: params.value,
              },
            };
          }
        }
        return undefined;
      },
    },
    {
      field: "matchType",
      headerName: this.translocoService.translate("targeting-asin-stats.match_type", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("targeting-asin-stats.match_type"),
      floatingFilter: true,
      filter: "agSetColumnFilter",
      cellStyle: { textAlign: "center" },
      cellRendererSelector: (params) => {
        if (params.node.isRowPinned()) return undefined;
        return {
          component: IGridBadgeComponent,
          params: {
            label: this.translocoService.translate(`keyword-segment-modal.${params.value.toLowerCase()}`),
            size: "xs",
            variant: "soft",
          },
        };
      },
      pinned: "left",
    },
    {
      headerName: this.translocoService.translate("targeting-asin-stats.sfr"),
      headerTooltip: this.translocoService.translate("search-term-ranks.search_frequency_rank"),
      colId: "SFR",
      valueGetter: (params: ValueGetterParams<AdStatsEx>) =>
        params.data?.matchType == MatchType.asinSameAs
          ? undefined
          : params.data?.keywordText
            ? (this.sfrByKeyword.get(params.data.keywordText) ?? this.SFR_FALLBACK)
            : this.SFR_FALLBACK,
      valueFormatter: (params: ValueFormatterParams<AdStatsEx>) => this.formatSFR(params.value, this.SFR_FALLBACK),
      filter: "agNumberColumnFilter",
      type: "numericColumn",
    },
    ...this.gridConfigService.getMetricsColDef(this.metrics).map(({ headerName, headerTooltip, ...def }: ColDef) => ({
      ...def,
      cellRendererParams: (params: ICellRendererParams<AdStatsEx>) => {
        return {
          ...def.cellRendererParams(params),
          previousData: undefined,
          currency: this.currency(),
          locale: this.locale(),
        };
      },
    })),
    {
      headerName: this.translocoService.translate("sp-strategy-group-page.tosro", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("sp-strategy-group-page.tosro"),
      colId: "TOSRO",
      headerTooltip: this.translocoService.translate("targeting-asin-stats.top_of_search_rankings_optimizer"),
      pinned: "right",
      suppressSizeToFit: true,
      floatingFilter: true,
      valueGetter: (params) => (this.topOfSearchRankingsOn.has(params.data.keywordText) ? "Enabled" : ""),
      cellRendererSelector: (params: ICellRendererParams<AdStatsEx>) => {
        const shouldDisplay = params.data?.matchType === MatchType.exact && !this.strategyEx()!.defaultStrategy;
        const isChecked = params.data?.keywordText
          ? this.topOfSearchRankingsOn.has(params.data.keywordText)
          : undefined;
        return shouldDisplay
          ? {
              component: IToggleGridComponent,
              params: {
                checked: isChecked,
                disabled: !this.supportedMarketplace || this.isReadOnly,
                tooltipValue: !this.strategyEx()!.isSpStrategy()
                  ? this.translocoService.translate("targeting-asin-stats.only_supported_for_sponsored_products")
                  : this.isReadOnly
                    ? this.translocoService.translate("targeting-asin-stats.not_enough_permissions")
                    : !this.supportedMarketplace
                      ? this.translocoService.translate("targeting-asin-stats.not_supported_for_this_marketplace")
                      : this.translocoService.translate("targeting-asin-stats.included_in_quota"),
                checkedChangeAction: () => {
                  this.switchOptimizerStatus(params.data!.keywordText!, !isChecked);
                },
              },
            }
          : undefined;
      },
      cellStyle: { "text-align": "center" },
    },
    {
      ...actionColumnProperties<AdStatsEx, string>(),
      cellRendererSelector: (params: ICellRendererParams<AdStatsEx>) => {
        if (params.node.isRowPinned()) return undefined;
        const actionBtns: ActionButton[] = [
          {
            icon: "chartLine",
            tooltip: this.translocoService.translate("targeting-asin-stats.display_keyword_chart"),
            disabled: params.data!.cost! <= 0,
            onClick: () => {
              this.selectedChartKwRow$.next(params.data);
            },
          },
          {
            icon: "list",
            tooltip: this.translocoService.translate("targeting-asin-stats.open_asin_details"),
            disabled: params.data!.cost! <= 0,
            onClick: () => {
              const asinData: AdStatsEx[] = this.asinDetailsByKeywordMatch!.get(this.keywordKey(params.data!))!;
              this.openAsinDetailsGridModal(asinData);
            },
          },
        ];

        return { component: ActionButtonsComponent, params: { actionButtons: actionBtns } };
      },
    },
  ];

  private defaultColDef: ColDef = {
    resizable: true,
    filter: true,
    sortable: true,
  };

  gridOptions: GridOptions = {
    ...gridConfig({ gridKey: this.GRID_KEY, withFooterRow: true }),
    columnDefs: this.withTOSROSwitch ? this.colDefs : this.colDefs.filter((c) => c.colId != "TOSRO"),
    defaultColDef: this.defaultColDef,
  };

  private asinDetailsGridOptions = {
    ...gridConfig({ autoSizeDefault: true, withFooterRow: true }),
    defaultColDef: this.defaultColDef,
    context: { parentComponent: this },
    columnDefs: [
      {
        ...ImageColumn,
        headerName: this.translocoService.translate("catalog-page.image", {}, "en"),
        headerValueGetter: () => this.translocoService.translate("catalog-page.image"),
        cellRendererSelector: (params: ICellRendererParams<AdStatsEx>) => {
          if (params.node.isRowPinned()) return undefined;
          return {
            component: ProductThumbnailGridComponent,
            params: {
              asin: params.data!.asin,
              marketplace: this.strategyEx()!.marketplace,
              smallImg: true,
            },
          };
        },
      },
      {
        colId: "asin",
        field: "asin",
        floatingFilter: true,
        filter: "agTextColumnFilter",
        headerName: this.translocoService.translate("common.asin", {}, "en"),
        headerValueGetter: () => this.translocoService.translate("common.asin"),
        width: 170,
        pinned: "left",
        enableRowGroup: true,
        cellRendererSelector: (params: ICellRendererParams<AdStatsEx>) => {
          return params.node.isRowPinned()
            ? null
            : {
                component: AsinLinkComponent,
                params: {
                  asin: params.data!.asin,
                  marketplace: this.strategyEx()!.marketplace,
                },
              };
        },
      },
      ...this.gridConfigService.getMetricsColDef(this.metrics).map(({ headerName, ...def }: ColDef) => ({
        ...def,
        cellRendererParams: (params: ICellRendererParams<AdStatsEx>) => {
          return {
            ...def.cellRendererParams(params),
            previousData: undefined,
            currency: this.currency(),
            locale: this.locale(),
          };
        },
      })),
      {
        ...actionColumnProperties<AdStatsEx, string>(),
        cellRendererSelector: (params: ICellRendererParams<AdStatsEx>) => {
          if (params.node.isRowPinned()) return undefined;
          const button: ActionButton = {
            icon: "chartLine",
            tooltip: this.translocoService.translate("targeting-asin-stats.display_asin_chart"),
            onClick: (_params) => {
              this.selectedChartKwRow$.next(params.node.data);
            },
          };
          return {
            component: ActionButtonsComponent,
            params: {
              actionButtons: [button],
            },
          };
        },
      },
    ] as ColDef[],
  };

  ngOnInit(): void {
    this.selectedMainMetrics$ = new BehaviorSubject<Metric<AdStatsEx>[]>([AD_SALES, COST]);

    this.sfrByKeyword.clear();
    this.topOfSearchRankingsOn.clear();

    const dateRange$: Observable<string[]> = this.userSelectionService.dateRange$;
    this.supportedMarketplace = keywordRankingAvailableFor(this.strategyEx()!.marketplace);
    const strategy = this.strategyEx()!;
    if (strategy.isSpStrategy() && strategy.topOfSearchRankings) {
      for (const k of strategy.topOfSearchRankings) {
        this.topOfSearchRankingsOn.add(k.keyword!);
      }
    }

    const stats$: Observable<AdStatsEx[]> = combineLatest([
      dateRange$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([range, currency]: [string[], Currency]) => {
        return this.statsService.getDailyTargetingAsinStats({
          accountId: this.strategyEx()!.accountId,
          marketplace: this.strategyEx()!.marketplace,
          minDate: range[0],
          maxDate: range[1],
          strategyId: this.strategyEx()!.strategyId,
          currency,
        });
      }),
      shareReplay(1),
    );

    const sfrList$: Observable<SearchTermRank[]> = stats$.pipe(
      switchMap<AdStatsEx[], Observable<Array<SearchTermRank>>>((x: AdStatsEx[]) => {
        const strategy = this.strategyEx()!;
        return x.length > 0
          ? this.brandAnalyticsService.getLastSearchFrequencyRank(
              x[0].marketplace!,
              x
                .flatMap((a) => a.keywordText!)
                .concat(strategy.isSpStrategy() ? strategy.topOfSearchRankings.map((x) => x.keyword!) : []),
            )
          : of([]);
      }),
    );

    // new data fetching
    combineLatest([stats$, sfrList$])
      .pipe(untilDestroyed(this))
      .subscribe(([stats, sfrList]: [AdStatsEx[], SearchTermRank[]]) => {
        const byDate = stats;

        // group keywords by title + match type
        const byKeywords: Map<string, AdStatsEx> = adStatsGroupBy<string>(byDate, (x) => this.keywordKey(x));
        const keywordsStats: AdStatsEx[] = Array.from(byKeywords.values());

        // ensure than top of search keywords are always displayed (even if no stats)
        const exactKwText = new Set(
          keywordsStats.filter((x) => x.matchType === MatchType.exact).map((x) => x.keywordText),
        );
        const strategy = this.strategyEx()!;
        if (strategy.isSpStrategy()) {
          strategy.topOfSearchRankings.forEach((x) => {
            if (!exactKwText.has(x.keyword)) {
              keywordsStats.push({
                keywordText: x.keyword,
                cost: 0,
                matchType: MatchType.exact,
                currency: this.currency(),
                marketplace: strategy.marketplace,
              });
              exactKwText.add(x.keyword);
            }
          });
        }

        this.kwStats = keywordsStats;

        this.asinDetailsByKeywordMatch = new Map();
        const asinDetailsMap: Map<string, AdStatsEx[]> = new Map();
        for (const d of byDate) {
          MapUtils.insertInArrayMap(asinDetailsMap, this.keywordKey(d), d);
        }

        // there is still multiple object with same asin for a keyword::matchType because of date range
        for (const [key, value] of asinDetailsMap) {
          // aggregate by asin, now we have on object by asin
          const aggData: AdStatsEx[] = Array.from(adStatsGroupBy<string>(value, (d: AdStatsEx) => d.asin!).values());
          this.asinDetailsByKeywordMatch.set(key, aggData);
        }

        // sfr values
        for (const sfr of sfrList) this.sfrByKeyword.set(sfr.searchTerm!, sfr.searchFrequencyRank!);

        // set asin to null to know if we toggle graph for keyword or asin
        this.gridData.set(keywordsStats.map((s: AdStatsEx) => ({ ...s, asin: undefined })));
        this.agGrid?.api.setRowData(this.gridData());
      });

    const historyStats$: Observable<KeywordTopOfSearchRankings[]> = this.userSelectionService.dateRange$.pipe(
      switchMap((dateRange: string[]) => {
        return this.statsService.getStrategyTopOfSearchRankingsHistory({
          accountId: this.strategyEx()!.accountId,
          marketplace: this.strategyEx()!.marketplace,
          minDate: dateRange[0],
          maxDate: dateRange[1],
          strategyId: this.strategyEx()!.strategyId,
        });
      }),
    );

    this.selectedChartKwRow$.pipe(untilDestroyed(this)).subscribe((rowData) => {
      if (!rowData) {
        return;
      }
      const chartData$ = combineLatest<[AdStatsEx[], KeywordTopOfSearchRankings[], Currency]>([
        stats$,
        historyStats$,
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        map(([data, history, currency]) => {
          // is the user selected row from keyword grid or asin details grid
          const isAsinRow = !!rowData.asin;

          const kwStats = data.filter(
            (x) =>
              x.matchType === rowData.matchType &&
              x.keywordText === rowData.keywordText &&
              (isAsinRow ? x.asin === rowData.asin : true),
          );

          // only available for MatchType.exact
          if (rowData.matchType === MatchType.exact) {
            history
              .filter((x) => x.keywordText === rowData.keywordText && (isAsinRow ? x.asin === rowData.asin : true))
              .filter((x) => x.factor && x.factor < 1)
              .forEach((h) => {
                const stats: AdStatsEx = {};
                stats.date = h.date;
                stats.keywordText = h.keywordText;
                stats.asin = h.asin;
                stats.matchType = MatchType.exact;
                stats.nbAsinsWithRuleFired = 1;
                stats.marketplace = h.marketplace;
                stats.currency = currency;
                kwStats?.push(stats);
              });
          }
          return {
            data: kwStats,
            totalData: kwStats?.reduce((total, curr) => mergeSeveralDates(total, curr), {}),
          };
        }),
      );
      const modalOption: ModalOptions<ChartModalInputData<AdStatsEx>> = {
        modalTitle: "Targeting Chart - " + rowData.keywordText,
        data: {
          locale: this.locale(),
          currency: this.currency(),
          dataSetConfiguration: {
            aggregationFunction: [sumAdStats],
          },
          chartData$: chartData$ as Observable<ChartData<AdStatsEx>>,
          selectedMetrics: [AD_SALES, COST],
          metrics:
            this.topOfSearchRankingsOn.has(rowData.keywordText!) && rowData.matchType === MatchType.exact
              ? this.metricsWithTopOfSearch
              : this.metrics,
          localStorageKey: { page: MetricsSelectorPage.asinTargeting },
        },
      };
      this.modalService.openModal<ChartModalInputData<AdStatsEx>, void>(ChartModalComponent, modalOption);
    });
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((x) => {
      this.selectedAccountMarketplace = x;
    });
  }

  openAsinDetailsGridModal(asinData: AdStatsEx[]) {
    const modalOptions: ModalOptions<GridViewerModalData> = {
      size: "xl",
      height: "fill",
      modalTitle: "Asin details - " + asinData[0].keywordText,
      data: {
        gridData: asinData as SubStrategyStats[],
        gridOptions: this.asinDetailsGridOptions,
      },
    };

    this.modalService.openModal<GridViewerModalData, void>(GridViewerComponent, modalOptions);
  }

  private formatSFR(value: number, fallback: number) {
    if (!value) return "-";
    return value < this.SFR_FALLBACK
      ? formatNumber(value, this.locale()!, "1.0-1")
      : ">" + formatNumber(fallback, this.locale(), "1.0-1");
  }

  switchOptimizerStatus(keywordText: string, enable: boolean): void {
    const strategy = this.strategyEx();
    if (!strategy || !strategy.isSpStrategy()) {
      return;
    }
    if (enable && this.topOfSearchRankingsOn.size > 50) {
      this.toasterService.error(
        "Error switching keyword state: limit of 50 keywords per strategy is reached",
        "Top Of Search Rankings Optimizer",
      );
      return;
    }

    const requestBody = new Array<string>();
    requestBody.push(keywordText);
    this.strategyService
      .updateStrategyTopOfSearchRankings({
        accountId: this.strategyEx()!.accountId,
        marketplace: this.strategyEx()!.marketplace,
        strategyId: this.strategyEx()!.strategyId,
        organizationId: this.selectedAccountMarketplace!.resourceOrganizationId!,
        action: enable
          ? UpdateStrategyTopOfSearchRankingsActionEnum.ADD
          : UpdateStrategyTopOfSearchRankingsActionEnum.DELETE,
        requestBody: requestBody,
      })
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          if (enable) {
            requestBody.forEach((k) => this.topOfSearchRankingsOn.add(k));
            if (this.strategyEx()!.isSpStrategy()) {
              requestBody.forEach((k) => strategy.topOfSearchRankings.push({ keyword: k }));
            }

            this.toasterService.success("Keyword ON", "Top Of Search Rankings Optimizer Activated");
          } else {
            requestBody.forEach((k) => this.topOfSearchRankingsOn.delete(k));
            if (this.strategyEx()!.isSpStrategy()) {
              requestBody.forEach((k) =>
                strategy.topOfSearchRankings.forEach((item, index) => {
                  if (item.keyword == k) strategy.topOfSearchRankings.splice(index, 1);
                }),
              );
            }
            this.toasterService.success("Keyword OFF", "Top Of Search Rankings Optimizer Deactivated");
          }
          this.agGrid?.api.refreshCells();
        },
        (error) => {
          this.toasterService.error(
            `Error switching keyword state: ${error?.response ? error.response.message : "Unknown error"}`,
            "Top Of Search Rankings Optimizer Update Error",
          );
        },
      );
  }

  private keywordKey(adStats: AdStatsEx): string {
    return adStats.keywordText + "::" + adStats.matchType;
  }

  restoreColumns() {
    restoreDefaultColumns(this.GRID_KEY, this.agGrid.api);
  }

  protected readonly STATUS_BAR = STATUS_BAR;
}
